import { map, Observable, combineLatest, shareReplay } from 'rxjs';
import { componentInterface } from '@ardoq/component-interface';
import {
  ContextToolBarAction,
  CurrentToolbarContext,
  ContextToolBarActionIds,
} from './types';
import { uniq } from 'lodash';
import {
  isTruthy,
  pluralizeWithCount,
  exhaustiveCheck,
} from '@ardoq/common-helpers';
import { loadedState$ } from 'loadedState/loadedState$';
import { getRepresentationDataWithColorVariantsForComponent } from 'viewpointBuilder/components/getRepresentationDataWithColorVariants';
import { isComponent } from '@ardoq/api-types';
import {
  ComponentSelection,
  isCreatedItemsLoadedState,
  isTraversalLoadedState,
  LoadedState,
  OrgAccessLevel,
} from '@ardoq/api-types';
import { sidebarPanel$ } from 'canvas/sidebarPanel/sidebarPanel$';
import {
  SidebarPanelState,
  SidebarPanelContentIds,
} from 'canvas/sidebarPanel/types';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import {
  permissionsOperations,
  type PermissionContextUser,
} from '@ardoq/access-control';
import {
  ActionsDisabledWhenNoComponentsSelected,
  ActionsDisabledWhenMultipleComponentsSelected,
  ActionsDisabledWhenMultipleTypesSelected,
  getDetailsAction,
  getCreateReferencesAction,
  getViewpointSwitcherAction,
  getContextSwitcherAction,
  AdminActions,
  WriterActions,
  ReaderActions,
  ContributorActions,
  getDatasetsAction,
} from './actions';
import { componentSelectionReloadBuffered$ } from './componentSelectionReloadBuffered$';
import { isDefaultWildcardTraversal } from 'traverseOneLevel/defaultWildcardViewpoint';
import { getStartTypeFromLoadedState } from './loadedStateHistory/rebuildStateFromPoppedHistoryUtils';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { t, type I18nLocale } from '@ardoq/i18n';
import { components$ } from 'streams/components/components$';

const getSelectionStatus = (i18nLocale: I18nLocale, selection: string[]) => {
  if (!selection.length) {
    return {
      label: t(i18nLocale, 'canvas:contextToolbarNoSelection', 'No selection'),
      isMultipleTypesSelected: false,
    };
  }
  if (selection.length === 1) {
    return {
      label:
        componentInterface.getDisplayName(selection[0]) ??
        t(
          i18nLocale,
          'canvas:contextToolbarUnknownComponent',
          'Unknown component'
        ),
      isMultipleTypesSelected: false,
    };
  }
  const types = uniq(selection.map(componentInterface.getTypeName)).filter(
    isTruthy
  );
  if (types.length === 1) {
    return {
      label: pluralizeWithCount(types[0], selection.length),
      isMultipleTypesSelected: false,
    };
  }
  return {
    label: t(
      i18nLocale,
      'canvas:contextToolbarComponentsOfTypes',
      { count: selection.length, typeCount: types.length },
      `${selection.length} components of ${types.length} types`
    ),
    isMultipleTypesSelected: true,
  };
};

const isButtonDisabled = (
  actionButton: ContextToolBarActionIds,
  isMultipleTypesSelected: boolean,
  currentSelection: string[]
) => {
  if (
    isMultipleTypesSelected &&
    ActionsDisabledWhenMultipleTypesSelected.has(actionButton)
  ) {
    return true;
  }
  if (
    currentSelection.length !== 1 &&
    ActionsDisabledWhenMultipleComponentsSelected.has(actionButton)
  ) {
    return true;
  }
  if (
    !currentSelection.length &&
    ActionsDisabledWhenNoComponentsSelected.has(actionButton)
  ) {
    return true;
  }
  return false;
};

const getActions = ({
  i18nLocale,
  actions,
  isMultipleTypesSelected,
  currentSelection,
  sidebarPanel,
}: {
  i18nLocale: I18nLocale;
  actions: ContextToolBarActionIds[];
  isMultipleTypesSelected: boolean;
  currentSelection: string[];
  sidebarPanel: SidebarPanelState;
}): ContextToolBarAction[] =>
  actions.map(actionButtonId => {
    switch (actionButtonId) {
      case ContextToolBarActionIds.CONTEXT_SWITCHER:
        return getContextSwitcherAction({
          i18nLocale,
          isDisabled: isButtonDisabled(
            actionButtonId,
            isMultipleTypesSelected,
            currentSelection
          ),
          isSelected:
            sidebarPanel.contentId === SidebarPanelContentIds.CONTEXT_SWITCHER,
        });
      case ContextToolBarActionIds.VIEWPOINT_SWITCHER:
        return getViewpointSwitcherAction({
          i18nLocale,
          isDisabled: isButtonDisabled(
            actionButtonId,
            isMultipleTypesSelected,
            currentSelection
          ),
          isSelected:
            sidebarPanel.contentId ===
            SidebarPanelContentIds.VIEWPOINT_SWITCHER,
        });
      case ContextToolBarActionIds.CREATE_REFERENCES:
        return getCreateReferencesAction({
          i18nLocale,
          isDisabled: isButtonDisabled(
            actionButtonId,
            isMultipleTypesSelected,
            currentSelection
          ),
          isSelected:
            sidebarPanel.contentId === SidebarPanelContentIds.CREATE_REFERENCES,
        });
      case ContextToolBarActionIds.DETAILS:
        return getDetailsAction({
          i18nLocale,
          isDisabled: isButtonDisabled(
            actionButtonId,
            isMultipleTypesSelected,
            currentSelection
          ),
          isSelected: sidebarPanel.contentId === SidebarPanelContentIds.DETAILS,
        });
      case ContextToolBarActionIds.DATASETS:
        return getDatasetsAction({
          i18nLocale,
          isDisabled: isButtonDisabled(
            actionButtonId,
            isMultipleTypesSelected,
            currentSelection
          ),
          isSelected:
            sidebarPanel.contentId === SidebarPanelContentIds.DATASETS,
        });
      default:
        return exhaustiveCheck(actionButtonId);
    }
  });

const getListOfAllowedActionsForUserRole = (
  user: PermissionContextUser | null
) => {
  if (!user) {
    return [];
  }

  const userOrgAccessLevel = permissionsOperations.getUserOrgAccessLevel(user);

  switch (userOrgAccessLevel) {
    case OrgAccessLevel.ADMIN:
      return AdminActions;
    case OrgAccessLevel.WRITER:
      return WriterActions;
    case OrgAccessLevel.READER:
      return ReaderActions;
    case OrgAccessLevel.CONTRIBUTOR:
      return ContributorActions;
    default:
      return exhaustiveCheck(userOrgAccessLevel);
  }
};

const getIsWildcardTraversal = (loadedState: LoadedState | null) => {
  if (!loadedState || !isTraversalLoadedState(loadedState)) return false;
  const {
    data: { paths },
  } = loadedState;
  return isDefaultWildcardTraversal(paths);
};

export type ContextToolBarState = {
  isToolBarHidden: boolean;
  currentContext: CurrentToolbarContext | null;
  actions: ContextToolBarAction[];
  isMultipleTypesSelected: boolean;
  hasNoSelection: boolean;
  showContextSummary: boolean;
  currentViewpointId: string | null;
  mainLoadedState: LoadedState;
  isWildcardTraversal: boolean;
  currentOpenSidebar: SidebarPanelContentIds | null;
};

const getComponentSelection = (
  loadedState: LoadedState,
  graphSelection: string[]
): ComponentSelection => {
  if (isCreatedItemsLoadedState(loadedState)) {
    return {
      startSet: loadedState.data.componentIds,
    };
  }
  if (graphSelection.length > 0) {
    return {
      startSet: graphSelection,
    };
  }
  return loadedState.data.componentSelection;
};

export const contextToolBar$: Observable<ContextToolBarState> = combineLatest({
  graphSelection: componentSelectionReloadBuffered$,
  loadedState: loadedState$,
  sidebarPanel: sidebarPanel$,
  currentUserPermissionContext: currentUserPermissionContext$,
  i18nLocale: i18nLocale$,
  components: components$,
}).pipe(
  map(
    ({
      graphSelection,
      loadedState: { loadedStates },
      sidebarPanel,
      currentUserPermissionContext,
      i18nLocale,
      components,
    }) => {
      const mainLoadedState = loadedStates[0];
      const currentSelection = graphSelection.length
        ? graphSelection
        : (mainLoadedState?.componentIdsAndReferences.startSetResult ?? []);

      const isWildcardTraversal = getIsWildcardTraversal(mainLoadedState);

      const listOfAllowedActionsForUserRole =
        getListOfAllowedActionsForUserRole(currentUserPermissionContext.user);

      if (!currentSelection.length || !mainLoadedState) {
        return {
          isToolBarHidden: !loadedStates.length,
          isMultipleTypesSelected: false,
          currentContext: null,
          actions: getActions({
            i18nLocale,
            actions: listOfAllowedActionsForUserRole,
            isMultipleTypesSelected: false,
            currentSelection,
            sidebarPanel,
          }),
          hasNoSelection: true,
          showContextSummary: false,
          currentViewpointId: null,
          mainLoadedState,
          isWildcardTraversal,
          currentOpenSidebar: sidebarPanel.contentId,
        };
      }

      const { isMultipleTypesSelected, label } = getSelectionStatus(
        i18nLocale,
        currentSelection
      );

      // TODO CL show something else if multiple types selected
      const selectedComponent = componentInterface.get(currentSelection[0]);
      const representationData = isComponent(selectedComponent)
        ? getRepresentationDataWithColorVariantsForComponent(
            selectedComponent,
            componentInterface.resolveComponentType(selectedComponent)
          )
        : null;

      const currentContext = {
        isGraphSelection: false,
        label,
        selectedIds: currentSelection,
        representationData,
        componentSelection: getComponentSelection(
          mainLoadedState,
          graphSelection
        ),
        startType: getStartTypeFromLoadedState(mainLoadedState, components),
      };

      return {
        isToolBarHidden: false,
        isMultipleTypesSelected,
        currentContext,
        hasNoSelection: false,
        showContextSummary: true,
        currentViewpointId:
          mainLoadedState && isTraversalLoadedState(mainLoadedState)
            ? (mainLoadedState.traversalId ?? null)
            : null,
        mainLoadedState,
        isWildcardTraversal,
        actions: getActions({
          i18nLocale,
          actions: listOfAllowedActionsForUserRole,
          isMultipleTypesSelected,
          currentSelection,
          sidebarPanel,
        }),
        currentOpenSidebar: sidebarPanel.contentId,
      };
    }
  ),
  shareReplay({ refCount: true, bufferSize: 1 })
);
